import { useMemo, useRef, useState } from 'react';
import { Divider, Input, Select } from 'antd';
import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { matchesSearch } from '@/utils/normalize';

interface Option {
  value: string;
  label: string;
  /** TCR-2572 — dòng phụ chữ xám dưới nhãn (vd chi nhánh của kho); tìm kiếm khớp cả dòng này. */
  description?: string;
}

/** TCR-2572 — dropdown tối thiểu rộng chừng này; nhãn dài hơn thì xuống dòng, không cắt "...". */
const MIN_POPUP_WIDTH = 360;

interface Props {
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  width: number;
  'data-testid'?: string;
}

/**
 * TCR-1987 — ô lọc có ô "Tìm kiếm" NHÌN THẤY ĐƯỢC ở đầu dropdown (như bản tham chiếu MISA).
 *
 * Trước đó ô Kho/Loại hàng hoá đã `showSearch` (gõ thẳng vào ô), nhưng khi mở ra ô chỉ
 * hiện chữ mờ của giá trị đang chọn — QA không biết là gõ được và báo "chưa có phần tìm
 * kiếm". Lọc vẫn bỏ dấu + không phân biệt hoa thường (`matchesSearch`).
 */
export function SearchableFilterSelect({
  value,
  options,
  onChange,
  width,
  'data-testid': testId,
}: Props) {
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<InputRef>(null);

  const visibleOptions = useMemo(
    () =>
      keyword
        ? options.filter(
            (o) =>
              matchesSearch(keyword, o.label) ||
              Boolean(o.description && matchesSearch(keyword, o.description)),
          )
        : options,
    [options, keyword],
  );

  return (
    <Select
      style={{ width }}
      value={value}
      options={visibleOptions}
      data-testid={testId}
      onChange={onChange}
      notFoundContent="Không tìm thấy"
      // TCR-2572 — trước đây dropdown rộng bằng ô (240px) và option một dòng: kho trùng tên
      // bị cắt thành "KHOHN003 - KHO SỈ ONLINE..." y hệt nhau, không phân biệt được.
      popupMatchSelectWidth={Math.max(width, MIN_POPUP_WIDTH)}
      optionRender={(option) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '20px' }}>
          <div>{option.data.label}</div>
          {option.data.description && (
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {option.data.description}
            </div>
          )}
        </div>
      )}
      onOpenChange={(open) => {
        if (open) {
          // Đợi dropdown mount xong mới focus được vào ô tìm kiếm bên trong.
          setTimeout(() => inputRef.current?.focus(), 0);
        } else {
          setKeyword('');
        }
      }}
      popupRender={(menu) => (
        <>
          <Input
            ref={inputRef}
            allowClear
            placeholder="Tìm kiếm"
            suffix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            // Không chặn thì Select nuốt phím (Space/Enter/mũi tên) để điều hướng option.
            onKeyDown={(e) => e.stopPropagation()}
            style={{ margin: '4px 4px 0', width: 'calc(100% - 8px)' }}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <Divider style={{ margin: '4px 0' }} />
          {menu}
        </>
      )}
    />
  );
}
